import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { LocalizationWrapper } from "~/components/LocalizationWrapper";
import { enableForEnv, Stages } from "~/lib/utils";
import { App } from "./App";
import AmplitudeProvider from "./components/3rd-party/AmplitudeProvider";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import "./index.css";
import logger from "./utils/logger";

// Only initialize Sentry for non-local environments
if (enableForEnv([Stages.PRODUCTION, Stages.STAGING, Stages.DEV])) {
  Sentry.init({
    dsn: "https://62863b3f9d82e5e10bb69ac70133a3a3@o4509435040890880.ingest.us.sentry.io/4509435042004992",
    environment: import.meta.env.VITE_STAGE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    _experiments: { enableLogs: true },
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event) {
      if (
        event.exception?.values?.[0]?.type === "CanceledError" &&
        event.exception?.values?.[0]?.value === "Token expired"
      ) {
        return null;
      }
      return event;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
});

logger.info("Frontend deployed at:", __BUILD_TIME__);

createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <LocalizationWrapper>
      <TooltipProvider>
        <AmplitudeProvider>
          <App />
        </AmplitudeProvider>
        <Toaster />
      </TooltipProvider>
    </LocalizationWrapper>
  </QueryClientProvider>
);
